import { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Check, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { joinWaitlist, getWaitlistCount } from '@/lib/api';
import { trackEvent } from '@/lib/metaPixel';

/**
 * Persistent top-of-app banner announcing the free-during-testing status
 * AND capturing emails for the launch waitlist inline (no page-jump needed).
 *
 * Behavior
 *   - Anonymous visitors see the message + inline email input.
 *   - Logged-in users see only the message (they're already customers) with
 *     a close button.
 *   - Dismissal (and successful submission) both persist to localStorage so
 *     returning visitors aren't nagged.
 *   - Because marketing pages use an absolute-positioned transparent header
 *     at top:0, we render this banner `fixed top-0` with a high z-index and
 *     mirror its height into `body { padding-top }` so the rest of the app
 *     is pushed down naturally.
 */
const DISMISS_KEY = 'propveiw_beta_banner_dismissed_v1';
const SUBMITTED_KEY = 'propveiw_beta_waitlist_submitted_v1';

export default function BetaBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [count, setCount] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
      if (localStorage.getItem(SUBMITTED_KEY) === '1') setSubmitted(true);
      setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  // Fetch the social-proof counter once. Silent-fail so the banner still
  // renders if the API hiccups.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getWaitlistCount();
        if (!cancelled && typeof data?.count === 'number') setCount(data.count);
      } catch {
        /* ignore — just hide the counter */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep `body { padding-top }` in sync with the banner height so the rest
  // of the app doesn't hide underneath the fixed banner.
  useEffect(() => {
    if (!visible) {
      document.body.style.paddingTop = '';
      return undefined;
    }
    const el = containerRef.current;
    if (!el) return undefined;

    const apply = () => {
      document.body.style.paddingTop = `${el.offsetHeight}px`;
    };
    apply();

    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', apply);
      document.body.style.paddingTop = '';
    };
  }, [visible, user, submitted]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode — banner will just reappear next reload */
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email');
      return;
    }
    setSaving(true);
    try {
      await joinWaitlist({ email, plan: 'unspecified', source: 'beta_banner' });
      trackEvent('Lead', { content_name: 'Waitlist - Beta Banner' });
      setSubmitted(true);
      setCount((c) => (typeof c === 'number' ? c + 1 : c));
      try { localStorage.setItem(SUBMITTED_KEY, '1'); } catch { /* ignore */ }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not sign you up — try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  const showForm = !user && !submitted;

  return (
    <div
      ref={containerRef}
      data-testid="beta-banner"
      className="fixed top-0 left-0 right-0 z-[100] w-full bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-[#0B1E3F] shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 pr-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-medium">
        <Sparkles className="h-4 w-4 flex-shrink-0" />
        <span className="text-center">
          <strong>Free during testing.</strong> PROP VEIW is in beta —{' '}
          {showForm ? 'get notified when paid plans launch.' : 'enjoy full access at no cost.'}
        </span>

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2"
            data-testid="beta-banner-form"
          >
            <input
              type="email"
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              disabled={saving}
              data-testid="beta-banner-email-input"
              className="h-8 rounded-md border border-[#0B1E3F]/30 bg-white/95 px-3 py-1 text-sm text-[#0B1E3F] placeholder-slate-400 shadow-inner outline-none focus:border-[#0B1E3F] focus:ring-2 focus:ring-[#0B1E3F]/40 min-w-[10rem] sm:min-w-[14rem]"
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="beta-banner-submit"
              className="h-8 rounded-md bg-[#0B1E3F] px-3 text-sm font-semibold text-amber-100 shadow-sm transition-colors hover:bg-[#0B1E3F]/90 disabled:opacity-60"
            >
              {saving ? '…' : 'Notify me'}
            </button>
            {typeof count === 'number' && count > 0 && (
              <span
                data-testid="beta-banner-count"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B1E3F]/80 whitespace-nowrap"
                title="People already on the waitlist"
              >
                <Users className="h-3 w-3" />
                {count.toLocaleString()} on the list
              </span>
            )}
          </form>
        )}

        {submitted && !user && (
          <span
            data-testid="beta-banner-success"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0B1E3F] px-2.5 py-1 text-xs font-semibold text-amber-100"
          >
            <Check className="h-3.5 w-3.5" />
            You&apos;re on the list — we&apos;ll email you.
          </span>
        )}

        {error && (
          <span
            className="text-xs font-semibold text-red-900"
            data-testid="beta-banner-error"
          >
            {error}
          </span>
        )}

        <button
          type="button"
          onClick={dismiss}
          data-testid="beta-banner-dismiss"
          aria-label="Dismiss beta banner"
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
